import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api, edenFetch } from "@/lib/eden";
import { inlineBrowseFor } from "@/lib/inline-browse";

/**
 * The category-by-path query behind the browse page header (`/cards/$`). Shared by
 * the page and the route loader, so a hovered category link (router
 * `defaultPreload: "intent"`) has usually fetched the header before the click — the
 * header then renders in the first frame instead of popping in above the grid.
 *
 * `keepPreviousData`: switching categories keeps the current header up until the
 * next one resolves (it re-flows in place) instead of collapsing it and pushing
 * the whole page up and back down.
 */
export const categoryBySlugQuery = (path: string) =>
  queryOptions({
    queryKey: ["/api/catalog/category/by-slug", path],
    queryFn: () => edenFetch(api.api.catalog.category["by-slug"].get({ query: { path } })),
    enabled: path.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    // A server-rendered `/cards/<path>` carries the category in the shell (lib/inline-browse.ts):
    // the header paints on mount instead of after the first API hop.
    initialData: () => inlineBrowseFor(path)?.category ?? undefined,
    initialDataUpdatedAt: () => inlineBrowseFor(path)?.loadedAt,
  });

/** Normalize the `/cards/$` splat into the category full_path the API expects. */
export const categoryPathFromSplat = (splat: string | undefined) =>
  (splat ?? "").replace(/^\/+|\/+$/g, "");
